<script setup lang="ts">
import { getPassageDetail } from '~/apis/data'
import { ModifyPassage } from '~/apis/passageStorage'
import { useTextareaAutoHeight } from '~/composables/useTextareaAutoHeight'
import { MAX_PASSAGE_LENGTH } from '~/common/globals'

// GA Analytics 추가
const { trackEvent } = useAnalytics()

const { $loading } = useNuxtApp()
const app = useAppStore()
const passageStorage = usePassageStorage()
// 새 API composable 사용
const { translatePassage } = useNewApi()

// textarea 자동 높이 조절
useTextareaAutoHeight()

// textarea 높이 수동 조절 함수
const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
  if (!element) return

  let initialHeight = parseInt(element.style.height, 10)
  if (isNaN(initialHeight) || initialHeight <= 0) {
    initialHeight = parseInt(getComputedStyle(element).height, 10) || 120
  }

  element.style.height = `${initialHeight}px`
  const maxHeight = parseInt(getComputedStyle(element).maxHeight, 10) || 220
  element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`
  element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

const passageDetailData = ref({
  edp_idx: '',
  edp_title: '',
  reg_date: '',
  passage_type: '',
  passage_type_name: '',
  passage: '',
  passage_kr: '',
  creturn: '',
  creturn_name: '',
})
const originalPassageContent = ref('')
const passageContentTextarea = ref<HTMLTextAreaElement | null>(null)
const koreanTranslationTextarea = ref<HTMLTextAreaElement | null>(null)

// 지문 내용 글자 수
const passageContentLength = computed(
  () => passageDetailData.value.passage.length,
)
// 지문 내용이 2500자를 초과하는지 확인
const isPassageContentTooLong = computed(
  () => passageContentLength.value > MAX_PASSAGE_LENGTH,
)
// **** 아래 값을 그대로 지문 내용에 넣음.. ***
const displayPassageContent = computed(() => {
  if (passageDetailData.value.creturn === 'N') {
    const processed = originalPassageContent.value
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    console.log('displayPassageContent - processed:', JSON.stringify(processed))
    return (passageDetailData.value.passage = processed)
  } else {
    return (passageDetailData.value.passage = originalPassageContent.value)
  }
})

// 지문 내용 변경 핸들러
const handlePassageInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  const value = target.value

  // 원본 내용 저장
  originalPassageContent.value = value

  // 현재 줄바꿈 타입에 따라 처리된 내용을 passageContent에 저장
  if (passageDetailData.value.creturn === 'N') {
    passageDetailData.value.passage = value
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  } else {
    passageDetailData.value.passage = value
  }

  console.log(
    'handlePassageInput - originalContent:',
    JSON.stringify(originalPassageContent.value),
  )
  console.log(
    'handlePassageInput - processedContent:',
    JSON.stringify(passageDetailData.value.passage),
  )

  /*  if (target.value.length > MAX_PASSAGE_LENGTH) {
    // 최대 길이를 초과하는 입력 방지
    passageDetailData.value.passage = target.value.slice(0, MAX_PASSAGE_LENGTH)
  }*/
}
// 지문 상세 조회
const passageDetail = async () => {
  const res = await getPassageDetail({
    UM_ID: app.user.UM_ID,
    EDP_IDX: passageStorage.state.value.passageInput.edp_idx,
  })
  if (res.data) {
    passageDetailData.value = res.data.data
    originalPassageContent.value = passageDetailData.value.passage

    // 초기 데이터 로드 후 textarea 높이 조절
    await nextTick()
    if (passageDetailData.value.passage && passageContentTextarea.value) {
      adjustTextareaHeight(passageContentTextarea.value)
    }
    if (passageDetailData.value.passage_kr && koreanTranslationTextarea.value) {
      adjustTextareaHeight(koreanTranslationTextarea.value)
    }
  } else {
    console.log('지문 상세 조회 실패')
  }
}
// save validation
const isSaveButtonEnabled = computed(() => {
  const data = passageDetailData.value
  return (
    data?.edp_title &&
    data.edp_title.trim() !== '' &&
    data?.passage &&
    data.passage.trim() !== '' &&
    data?.passage_kr &&
    data.passage_kr.trim() !== '' &&
    !isPassageContentTooLong.value
  )
})
// 지문 수정 저장
const savePassage = async () => {
  if (!isSaveButtonEnabled.value) return

  const saveStartTime = Date.now()

  try {
    $loading.show()

    const res = await ModifyPassage({
      EDP_IDX: passageDetailData.value.edp_idx,
      UM_ID: app.user.UM_ID,
      CRETURN_YN: passageDetailData.value.creturn,
      TITLE: passageDetailData.value.edp_title,
      PASSAGE: passageDetailData.value.passage,
      PASSAGE_KR: passageDetailData.value.passage_kr,
    })
    if (res.data) {
      // GA 이벤트: 지문 수정 완료
      try {
        trackEvent('passagestorage_passage_modified', {
          event_category: 'item_interaction',
          passage_id: passageDetailData.value.edp_idx,
          passage_title: passageDetailData.value.edp_title,
          passage_length: passageDetailData.value.passage.length,
          translation_length: passageDetailData.value.passage_kr.length,
          passage_type: passageDetailData.value.passage_type_name,
          creturn_type: passageDetailData.value.creturn_name,
          save_time_ms: Date.now() - saveStartTime,
          had_translation_changes: true, // 번역 변경 여부는 별도 추적 가능
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString(),
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }

      passageStorage.closePassageInput()
      passageStorage.reload()
    }
  } catch (err) {
    console.log('[ERROR] 지문 수정 오류 - ', err)
  } finally {
    $loading.hide()
  }
}

// 편집 취소 핸들러
const handleCancelEdit = () => {
  // GA 이벤트: 편집 취소
  try {
    trackEvent('passagestorage_edit_cancelled', {
      event_category: 'ui_interaction',
      passage_id: passageDetailData.value.edp_idx,
      passage_title: passageDetailData.value.edp_title,
      had_changes: true, // 변경사항 유무 (현재는 true로 고정, 필요시 변경 추적 로직 추가 가능)
      passage_length: passageDetailData.value.passage.length,
      translation_length: passageDetailData.value.passage_kr.length,
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }

  passageStorage.closePassageInput()
}

/**
 * AI 번역 버튼 클릭 핸들러
 * 지문 내용을 AI를 통해 한글로 번역
 */
const handleAITranslateClick = async () => {
  // 지문 내용이 없으면 실행하지 않음
  if (!passageDetailData.value.passage.trim()) return

  const translateStartTime = Date.now()

  // GA 이벤트: AI 번역 버튼 클릭
  try {
    trackEvent('passagestorage_ai_translate_clicked', {
      event_category: 'ai_feature',
      passage_id: passageDetailData.value.edp_idx,
      passage_length: passageDetailData.value.passage.trim().length,
      has_existing_translation: !!passageDetailData.value.passage_kr.trim(),
      passage_type: passageDetailData.value.passage_type_name,
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }

  // 로딩 시작
  $loading.show()
  try {
    // 번역 API 호출
    const response = await translatePassage({
      UM_ID: app.user.UM_ID,
      PASSAGE: passageDetailData.value.passage.trim(),
    })

    console.log('Translation response:', response)

    // 번역 결과를 직접 한글 해석 필드에 설정
    // 새 Lambda API는 response.data 구조로 응답
    if (response?.data) {
      passageDetailData.value.passage_kr = response.data

      // GA 이벤트: AI 번역 성공
      try {
        trackEvent('passagestorage_ai_translate_success', {
          event_category: 'ai_feature',
          passage_id: passageDetailData.value.edp_idx,
          passage_length: passageDetailData.value.passage.trim().length,
          translation_length: response.data.length,
          translation_time_ms: Date.now() - translateStartTime,
          passage_type: passageDetailData.value.passage_type_name,
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString(),
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }
    } else if (response) {
      // 직접 응답인 경우
      passageDetailData.value.passage_kr = response

      // GA 이벤트: AI 번역 성공
      try {
        trackEvent('passagestorage_ai_translate_success', {
          event_category: 'ai_feature',
          passage_id: passageDetailData.value.edp_idx,
          passage_length: passageDetailData.value.passage.trim().length,
          translation_length: String(response).length,
          translation_time_ms: Date.now() - translateStartTime,
          passage_type: passageDetailData.value.passage_type_name,
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString(),
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }
    } else {
      passageDetailData.value.passage_kr = '번역 결과를 받지 못했습니다.'

      // GA 이벤트: AI 번역 결과 없음
      try {
        trackEvent('passagestorage_ai_translate_no_result', {
          event_category: 'ai_feature',
          passage_id: passageDetailData.value.edp_idx,
          passage_length: passageDetailData.value.passage.trim().length,
          translation_time_ms: Date.now() - translateStartTime,
          passage_type: passageDetailData.value.passage_type_name,
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString(),
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }
    }

    // 한글 해석 textarea 높이 조절
    await nextTick()
    if (koreanTranslationTextarea.value) {
      adjustTextareaHeight(koreanTranslationTextarea.value)
    }
  } catch (error) {
    // console.error('Translation failed:', error)
    // 에러 발생 시 사용자에게 알림
    passageDetailData.value.passage_kr =
      '번역에 실패했습니다. 다시 시도해주세요.'

    // GA 이벤트: AI 번역 실패
    try {
      trackEvent('passagestorage_ai_translate_fail', {
        event_category: 'ai_feature',
        passage_id: passageDetailData.value.edp_idx,
        passage_length: passageDetailData.value.passage.trim().length,
        translation_time_ms: Date.now() - translateStartTime,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        passage_type: passageDetailData.value.passage_type_name,
        um_idx: app.user.UM_IDX ?? '',
        timestamp: new Date().toISOString(),
      })
    } catch (trackingError) {
      console.warn('GA tracking failed:', trackingError)
    }

    // 에러 메시지에 대해서도 textarea 높이 조절
    await nextTick()
    if (koreanTranslationTextarea.value) {
      adjustTextareaHeight(koreanTranslationTextarea.value)
    }
  } finally {
    // 로딩 종료
    $loading.hide()
  }
}

onMounted(async () => {
  // GA 이벤트: 편집 시작
  try {
    trackEvent('passagestorage_edit_started', {
      event_category: 'item_interaction',
      passage_id: passageStorage.state.value.passageInput.edp_idx,
      action_source: 'edit_button_click',
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }

  await passageDetail()
})
// lineBreakType 변경 시 passageContent 업데이트
watch(
  () => passageDetailData.value.creturn,
  (newType) => {
    console.log('lineBreakType watch - newType:', newType)
    if (originalPassageContent.value) {
      if (newType === 'N') {
        passageDetailData.value.passage = originalPassageContent.value
          .replace(/[\r\n]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      } else {
        passageDetailData.value.passage = originalPassageContent.value
      }
      console.log('lineBreakType watch - newType:', newType)
      console.log(
        'lineBreakType watch - updated passageContent:',
        JSON.stringify(passageDetailData.value.passage),
      )
    }
  },
)
</script>

<template>
  <!-- 지문 수정 -->
  <div class="layer active">
    <div class="mask" @click="handleCancelEdit"></div>
    <div class="inner">
      <section class="sample fullHeight">
        <header class="">
          <h2>지문 수정</h2>
          <a class="layerClose" @click="handleCancelEdit">닫기</a>
        </header>
        <main>
          <div class="writeType01">
            <dl>
              <dt>줄바꿈 유형</dt>
              <dd>
                <label class="label">
                  <input
                    v-model="passageDetailData.creturn"
                    type="radio"
                    name="lineBreakType"
                    value="N"
                  />
                  줄바꿈 제거
                </label>
                <label class="label">
                  <input
                    v-model="passageDetailData.creturn"
                    type="radio"
                    name="lineBreakType"
                    value="Y"
                  />
                  줄바꿈 유지
                </label>
              </dd>
            </dl>
            <dl>
              <dt>지문 제목</dt>
              <dd>
                <input
                  type="text"
                  placeholder="지문 제목을 입력해주세요"
                  v-model="passageDetailData.edp_title"
                />
              </dd>
            </dl>
            <dl>
              <dt>지문 내용</dt>
              <dd>
                <label class="textareaWrap">
                  <textarea
                    ref="passageContentTextarea"
                    :placeholder="`지문 입력 주의사항 &#10;(1), (가), (A)와 같은 번호나 기호는 모두 제거해 주세요.&#10;${MAX_PASSAGE_LENGTH}자가 넘는 긴 지문은 나눠서 넣어주세요.&#10;오타, 철자 오류, 어법 오류는 수정해 올바른 문장으로 입력해 주세요.&#10;한글 내용, 특수 기호는 자동으로 삭제됩니다.`"
                    class="textareaAutoHeight"
                    v-model="passageDetailData.passage"
                    @input="handlePassageInput"
                    :maxlength="MAX_PASSAGE_LENGTH"
                  >
                  </textarea>
                  <span class="count">
                    <span :class="{ error: isPassageContentTooLong }">{{
                      passageContentLength
                    }}</span>
                    <span>/</span> {{ MAX_PASSAGE_LENGTH }}
                  </span>
                </label>
              </dd>
              <dd class="notice">
                주의사항을 지키지 않는 경우 문제 결과에 오류가 있을 수 있습니다.
              </dd>
            </dl>
            <dl>
              <dt>한글 해석</dt>
              <dd>
                <label class="textareaWrap">
                  <textarea
                    ref="koreanTranslationTextarea"
                    placeholder="한글 해석을 직접 입력하거나 AI로 번역해보세요."
                    class="textareaAutoHeight"
                    v-model="passageDetailData.passage_kr"
                  >
                  </textarea>
                  <span></span>
                </label>
                <a
                  class="aiTranslate"
                  @click="handleAITranslateClick"
                  :class="{ disabled: !passageDetailData.passage.trim() }"
                >
                  AI 번역하기
                </a>
              </dd>
            </dl>
          </div>
        </main>
        <footer>
          <div class="btnWrap">
            <a
              :class="['btnStyle', 'wide', { disable: !isSaveButtonEnabled }]"
              @click="savePassage"
            >
              수정완료
            </a>
          </div>
        </footer>
      </section>
    </div>
  </div>
  <!-- /지문 수정 -->
</template>

<style scoped></style>
